/**
 * SMTP Settings Panel Component
 * Editable SMTP configuration with test connection, send test email, and save
 */

import { useState, useEffect } from 'react';
import { useSystemSettings, useUpdateSettings } from '@/hooks/useUploadSettings';
import { asApiError } from '@/utils/error.util';
import type { SettingValue } from '@/hooks/useUploadSettings';
import { useSMTPStatus } from '@/hooks/useInvitations';
import apiClient from '@/services/api.client';

interface SmtpFormData {
  smtp_host: string;
  smtp_port: number;
  smtp_secure: boolean;
  smtp_user: string;
  smtp_password: string;
  smtp_from: string;
  smtp_from_name: string;
}

const SMTPStatusPanel = () => {
  const { data: allSettings, isLoading, error } = useSystemSettings('email');
  const { data: smtpStatus } = useSMTPStatus();
  const updateSettings = useUpdateSettings();

  const [formData, setFormData] = useState<SmtpFormData>({
    smtp_host: '',
    smtp_port: 587,
    smtp_secure: false,
    smtp_user: '',
    smtp_password: '',
    smtp_from: '',
    smtp_from_name: 'HighFive',
  });
  const [hasExistingPassword, setHasExistingPassword] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendTestResult, setSendTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Initialize form from fetched settings, falling back to resolved SMTP status (env vars)
  useEffect(() => {
    if (allSettings) {
      setFormData({
        smtp_host: (allSettings.smtp_host?.value as string) || smtpStatus?.host || '',
        smtp_port: (allSettings.smtp_port?.value as number) || smtpStatus?.port || 587,
        smtp_secure: (allSettings.smtp_secure?.value as boolean) ?? smtpStatus?.secure ?? false,
        smtp_user: (allSettings.smtp_user?.value as string) || smtpStatus?.user || '',
        smtp_password: '', // Never pre-filled
        smtp_from: (allSettings.smtp_from?.value as string) || smtpStatus?.from || '',
        smtp_from_name: (allSettings.smtp_from_name?.value as string) || smtpStatus?.fromName || 'HighFive',
      });
      setHasExistingPassword(!!(allSettings.smtp_password as { hasPassword?: boolean })?.hasPassword || !!smtpStatus?.hasPassword);
    }
  }, [allSettings, smtpStatus]);

  // Use resolved status (DB + env) for the badge — not just form data
  const isConfigured = smtpStatus?.configured ?? !!(formData.smtp_host && formData.smtp_user && (formData.smtp_password || hasExistingPassword));

  const handleChange = (field: keyof SmtpFormData, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSaveStatus('idle');
    setTestResult(null);
    setSendTestResult(null);
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    setSaveError('');
    try {
      const payload: Record<string, SettingValue> = {
        smtp_host: formData.smtp_host,
        smtp_port: formData.smtp_port,
        smtp_secure: formData.smtp_secure,
        smtp_user: formData.smtp_user,
        smtp_from: formData.smtp_from,
        smtp_from_name: formData.smtp_from_name,
      };
      if (formData.smtp_password) {
        payload.smtp_password = formData.smtp_password;
      }
      await updateSettings.mutateAsync(payload);
      setSaveStatus('success');
      if (formData.smtp_password) {
        setHasExistingPassword(true);
        setFormData(prev => ({ ...prev, smtp_password: '' }));
      }
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : 'Грешка при запазване');
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await apiClient.post('/admin/smtp/test', {
        smtp_host: formData.smtp_host,
        smtp_port: formData.smtp_port,
        smtp_secure: formData.smtp_secure,
        smtp_user: formData.smtp_user,
        smtp_password: formData.smtp_password || undefined,
        smtp_from: formData.smtp_from,
        smtp_from_name: formData.smtp_from_name,
      });
      setTestResult({ success: true, message: response.data.message || 'SMTP връзката е успешна!' });
    } catch (err) {
      const message = asApiError(err).response?.data?.message || asApiError(err).message || 'Неуспешно свързване';
      setTestResult({ success: false, message });
    } finally {
      setTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTest(true);
    setSendTestResult(null);
    try {
      const response = await apiClient.post('/admin/smtp/send-test');
      setSendTestResult({ success: true, message: response.data.message || 'Тестов имейл изпратен!' });
    } catch (err) {
      const message = asApiError(err).response?.data?.message || asApiError(err).message || 'Неуспешно изпращане';
      setSendTestResult({ success: false, message });
    } finally {
      setSendingTest(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-discord-bg rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-discord-bg-skeleton rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-discord-bg-skeleton rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
        <p className="text-red-400">Грешка при зареждане на SMTP настройките</p>
      </div>
    );
  }

  return (
    <div className="bg-discord-bg rounded-lg">
      <div className="px-6 py-4 border-b border-discord-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-discord-text-heading">SMTP настройки</h3>
            <p className="text-sm text-discord-text-muted mt-1">
              Конфигурация на имейл сървъра за покани и известия
            </p>
          </div>
          {isConfigured ? (
            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-500/20 text-green-400">
              Конфигуриран
            </span>
          ) : (
            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-red-500/20 text-red-400">
              Не е конфигуриран
            </span>
          )}
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-discord-text-muted mb-1">SMTP Хост</label>
            <input
              type="text"
              value={formData.smtp_host}
              onChange={e => handleChange('smtp_host', e.target.value)}
              placeholder="mail.example.com"
              className="w-full px-3 py-2 border border-discord-border rounded-lg bg-discord-bg-deep text-discord-text-normal placeholder-discord-text-faint focus:outline-none focus:border-discord-blurple"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-discord-text-muted mb-1">SMTP Порт</label>
            <input
              type="number"
              value={formData.smtp_port}
              onChange={e => handleChange('smtp_port', parseInt(e.target.value) || 587)}
              className="w-full px-3 py-2 border border-discord-border rounded-lg bg-discord-bg-deep text-discord-text-normal focus:outline-none focus:border-discord-blurple"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-discord-text-muted mb-1">SMTP Потребител</label>
            <input
              type="text"
              value={formData.smtp_user}
              onChange={e => handleChange('smtp_user', e.target.value)}
              placeholder="user@example.com"
              className="w-full px-3 py-2 border border-discord-border rounded-lg bg-discord-bg-deep text-discord-text-normal placeholder-discord-text-faint focus:outline-none focus:border-discord-blurple"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-discord-text-muted mb-1">SMTP Парола</label>
            <input
              type="password"
              value={formData.smtp_password}
              onChange={e => handleChange('smtp_password', e.target.value)}
              placeholder={hasExistingPassword ? '••••••••' : 'Въведете парола'}
              className="w-full px-3 py-2 border border-discord-border rounded-lg bg-discord-bg-deep text-discord-text-normal placeholder-discord-text-faint focus:outline-none focus:border-discord-blurple"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-discord-text-muted mb-1">Име на изпращача</label>
            <input
              type="text"
              value={formData.smtp_from_name}
              onChange={e => handleChange('smtp_from_name', e.target.value)}
              placeholder="HighFive"
              className="w-full px-3 py-2 border border-discord-border rounded-lg bg-discord-bg-deep text-discord-text-normal placeholder-discord-text-faint focus:outline-none focus:border-discord-blurple"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-discord-text-muted mb-1">Адрес на изпращача</label>
            <input
              type="email"
              value={formData.smtp_from}
              onChange={e => handleChange('smtp_from', e.target.value)}
              placeholder="noreply@example.com"
              className="w-full px-3 py-2 border border-discord-border rounded-lg bg-discord-bg-deep text-discord-text-normal placeholder-discord-text-faint focus:outline-none focus:border-discord-blurple"
            />
          </div>
        </div>

        {/* TLS Toggle */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-discord-text-muted mb-2">Сигурност на връзката</label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => handleChange('smtp_secure', false)}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                !formData.smtp_secure
                  ? 'bg-discord-blurple border-discord-blurple text-white'
                  : 'bg-discord-bg-deep border-discord-border text-discord-text-muted hover:border-discord-blurple'
              }`}
            >
              STARTTLS (порт 587)
            </button>
            <button
              type="button"
              onClick={() => handleChange('smtp_secure', true)}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                formData.smtp_secure
                  ? 'bg-discord-blurple border-discord-blurple text-white'
                  : 'bg-discord-bg-deep border-discord-border text-discord-text-muted hover:border-discord-blurple'
              }`}
            >
              TLS/SSL (порт 465)
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3 pt-4 border-t border-discord-border">
          <button
            onClick={handleSave}
            disabled={saveStatus === 'saving'}
            className="px-4 py-2 text-sm font-medium text-white bg-discord-blurple hover:bg-discord-blurple-hover rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveStatus === 'saving' ? 'Запазване...' : 'Запази настройки'}
          </button>

          <button
            onClick={handleTestConnection}
            disabled={testing || !formData.smtp_host || !formData.smtp_user}
            className="px-4 py-2 text-sm font-medium text-white bg-discord-green hover:bg-discord-green-hover rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testing ? 'Тестване...' : 'Тестване на връзката'}
          </button>

          <button
            onClick={handleSendTestEmail}
            disabled={sendingTest || !isConfigured}
            title="Изпраща тестов имейл до вашия адрес (запазете настройките първо)"
            className="px-4 py-2 text-sm font-medium text-white bg-discord-amber hover:bg-discord-amber-hover rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sendingTest ? 'Изпращане...' : 'Изпрати тестов имейл'}
          </button>

          {saveStatus === 'success' && (
            <span className="px-3 py-1.5 text-sm font-medium rounded-lg bg-green-500/20 text-green-400">
              Настройките са запазени
            </span>
          )}

          {saveStatus === 'error' && (
            <span className="px-3 py-1.5 text-sm font-medium rounded-lg bg-red-500/20 text-red-400">
              {saveError}
            </span>
          )}
        </div>

        {/* Test connection result */}
        {testResult && (
          <div
            className={`mt-3 px-4 py-2 rounded-lg text-sm font-medium ${
              testResult.success
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-red-500/20 text-red-400 border border-red-500/30'
            }`}
          >
            {testResult.message}
          </div>
        )}

        {/* Send test email result */}
        {sendTestResult && (
          <div
            className={`mt-3 px-4 py-2 rounded-lg text-sm font-medium ${
              sendTestResult.success
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-red-500/20 text-red-400 border border-red-500/30'
            }`}
          >
            {sendTestResult.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default SMTPStatusPanel;
